import {ContentItem} from '../../types/models/ContentItem';
import {IError} from '../../@template/helpers/errors';
import {ContentItemSearchRequest, defaultForContentItemSearchRequest} from '../../types/requests/ContentItemSearchRequest';
import {DefaultSortByOptions} from '../../types/common/SortByOptions/DefaultSortByOptions';
import {SortOrderOptions} from '../../types/common/SortOrderOptions';
import {PagedEntityListOfContentItemSearchItem} from '../../types/views/PagedEntityListOfContentItemSearchItem';
import {createReducer, PayloadAction} from '@reduxjs/toolkit';
import { CONTENT_ITEM_SEARCH, CONTENT_ITEMS } from './Actions';
import {SearchRequest} from '../../types/common/SearchRequest';
import { ContentItemSearchItem } from "../../types/views/ContentItemSearchItem";

export interface IContentItemState {
    contentItem: ContentItem | undefined;
    contentItemLoading: boolean | false;
    contentItemLoadError: string | undefined;
    contentItemSaving: boolean | false;
    contentItemSaveError: IError | undefined;
    contentItemSearchParams: ContentItemSearchRequest;
    contentItemSearchSortOption: DefaultSortByOptions;
    contentItemSearchSortOrder: SortOrderOptions;
    contentItemsResult: PagedEntityListOfContentItemSearchItem | undefined;
    contentItemsSearching: boolean;
    contentItemSearchError: string | undefined;
    contentItemContentItemsResult: PagedEntityListOfContentItemSearchItem | undefined;
    contentItemContentItemsSearching: boolean;
    contentItemContentItemsError: string | undefined;
    contentItemsSelected: ContentItemSearchItem[] | undefined;
}

const initialState: IContentItemState = {
    contentItem: undefined,
    contentItemLoading: false,
    contentItemLoadError: undefined,
    contentItemSaving: false,
    contentItemSaveError: undefined,
    contentItemSearchParams: defaultForContentItemSearchRequest(),
    contentItemSearchSortOption: DefaultSortByOptions.Relevance,
    contentItemSearchSortOrder: SortOrderOptions.Desc,
    contentItemsResult: undefined,
    contentItemsSearching: false,
    contentItemSearchError: undefined,
    contentItemContentItemsResult: undefined,
    contentItemContentItemsSearching: false,
    contentItemContentItemsError: undefined,
    contentItemsSelected: undefined,
}

const contentItemReducer = createReducer(initialState, {
    [CONTENT_ITEMS.LOAD]: (state) => {
        state.contentItem = undefined;
        state.contentItemLoading = true;
        state.contentItemLoadError = undefined;
        state.contentItemSaveError = undefined;
    },
    [CONTENT_ITEMS.REFRESH]: (state) => {
        state.contentItemLoading = false;
        state.contentItemLoadError = undefined;
        state.contentItemSaveError = undefined;
    },
    [CONTENT_ITEMS.LOADED]: (state, action: PayloadAction<ContentItem>) => {
        state.contentItemLoading = false;
        state.contentItemLoadError = undefined;
        state.contentItem = action.payload;
    },
    [CONTENT_ITEMS.LOAD_ERROR]: (state, action: PayloadAction<string>) => {
        state.contentItemLoading = false;
        state.contentItemLoadError = action.payload;
        state.contentItem = undefined;
    },
    [CONTENT_ITEMS.SAVE]: (state) => {
        state.contentItemSaving = true;
        state.contentItemSaveError = undefined;
    },
    [CONTENT_ITEMS.SAVED]: (state, action: PayloadAction<ContentItem>) => {
        state.contentItemSaving = false;
        state.contentItemSaveError = undefined;
        state.contentItem = action.payload;
    },
    [CONTENT_ITEMS.SAVE_ERROR]: (state, action: PayloadAction<IError>) => {
        state.contentItemSaving = false;
        state.contentItemSaveError = action.payload;
    },
    [CONTENT_ITEM_SEARCH.LOAD]: (state, action: PayloadAction<SearchRequest<ContentItemSearchRequest>>) => {
        state.contentItemSearchParams = action.payload.searchParams;
        state.contentItemSearchSortOption = action.payload.sortBy??(DefaultSortByOptions.Relevance);
        state.contentItemSearchSortOrder = action.payload.sortDirection??(SortOrderOptions.Desc);
        state.contentItemsSearching = true;
        state.contentItemSearchError = undefined;
    },
    [CONTENT_ITEM_SEARCH.ERROR]: (state, action: PayloadAction<string>) => {
        state.contentItemsSearching = false;
        state.contentItemSearchError = action.payload;
    },
    [CONTENT_ITEM_SEARCH.LOADED]: (state, action: PayloadAction<PagedEntityListOfContentItemSearchItem>) => {
        state.contentItemsSearching = false;
        state.contentItemSearchError = undefined;
        state.contentItemsResult = action.payload;
    },
    [CONTENT_ITEM_SEARCH.SELECT_ITEM]: (state, action: PayloadAction<ContentItemSearchItem>) => {
        let itemsSelected: ContentItemSearchItem[] = state.contentItemsSelected ?? [];
        if (itemsSelected?.find(x => x.id === action.payload.id)) {
            itemsSelected = itemsSelected?.filter(x => x.id !== action.payload.id);
        } else {
            itemsSelected = [...itemsSelected, action.payload];
        }
        state.contentItemsSelected = itemsSelected;
    },
    [CONTENT_ITEM_SEARCH.SET_SELECTION]: (state, action: PayloadAction<ContentItemSearchItem[]>) => {
        state.contentItemsSelected = action.payload;
    },
    [CONTENT_ITEM_SEARCH.CLEAR_SELECTION]: (state, action: PayloadAction<string>) => {
        state.contentItemsSelected = [];
    }
});

export default contentItemReducer;